import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Shield, Users, ShoppingBag, Building2, UserCheck, MapPin, AlertTriangle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMFAPolicy } from '@/hooks/useMFAPolicy';

interface PolicyToggle {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  policyKey: keyof ReturnType<typeof useMFAPolicy>['policy'];
}

const POLICY_TOGGLES: PolicyToggle[] = [
  {
    key: 'mfa_required_all',
    label: 'Tous les utilisateurs',
    description: 'Force le MFA pour absolument tous les comptes — prioritaire sur les réglages par groupe.',
    icon: <Users className="h-4 w-4" />,
    policyKey: 'requireAll',
  },
  {
    key: 'mfa_required_admins',
    label: 'Administrateurs',
    description: 'Comptes admin et super_admin.',
    icon: <Shield className="h-4 w-4" />,
    policyKey: 'requireAdmins',
  },
  {
    key: 'mfa_required_employees',
    label: 'Employés (RH)',
    description: 'Comptes avec le rôle hr.',
    icon: <UserCheck className="h-4 w-4" />,
    policyKey: 'requireEmployees',
  },
  {
    key: 'mfa_required_vendors',
    label: 'Vendeurs',
    description: 'Comptes vendeurs approuvés.',
    icon: <ShoppingBag className="h-4 w-4" />,
    policyKey: 'requireVendors',
  },
  {
    key: 'mfa_required_relays',
    label: 'Points relais',
    description: 'Opérateurs de points relais.',
    icon: <MapPin className="h-4 w-4" />,
    policyKey: 'requireRelays',
  },
  {
    key: 'mfa_required_clients',
    label: 'Clients',
    description: 'Utilisateurs clients standard.',
    icon: <Building2 className="h-4 w-4" />,
    policyKey: 'requireClients',
  },
];

export function MFASettingsManager() {
  const { policy, loading, refetch } = useMFAPolicy();
  const [saving, setSaving] = useState<string | null>(null);
  const [trustedDays, setTrustedDays] = useState(60);
  const [savingDays, setSavingDays] = useState(false);
  const { toast } = useToast();

  // Charger la durée configurée
  useEffect(() => {
    supabase.from('site_settings').select('value').eq('key', 'mfa_trusted_device_days').maybeSingle()
      .then(({ data }) => { if (data) setTrustedDays(Number((data as any).value ?? 60)); });
  }, []);

  const saveTrustedDays = async () => {
    setSavingDays(true);
    await supabase.from('site_settings').upsert(
      { key: 'mfa_trusted_device_days', value: trustedDays, category: 'security', data_type: 'number' },
      { onConflict: 'key' }
    );
    toast({ title: 'Durée sauvegardée', description: `Les appareils seront mémorisés ${trustedDays} jours.` });
    setSavingDays(false);
  };

  const handleToggle = async (key: string, newValue: boolean) => {
    setSaving(key);
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { key, value: newValue, category: 'security', data_type: 'boolean' },
          { onConflict: 'key' }
        );

      if (error) throw error;

      await refetch();
      toast({
        title: 'Paramètre MFA mis à jour',
        description: `La politique MFA a été ${newValue ? 'activée' : 'désactivée'}.`,
      });
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || 'Impossible de mettre à jour la politique MFA.',
        variant: 'destructive',
      });
    } finally {
      setSaving(null);
    }
  };

  const anyEnabled = Object.values(policy).some(Boolean);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Authentification à deux facteurs (MFA)
        </CardTitle>
        <CardDescription>
          Configurez quels groupes d'utilisateurs doivent obligatoirement activer le MFA (TOTP — Google
          Authenticator, Microsoft Authenticator, Authy…).
        </CardDescription>
        {anyEnabled && (
          <div className="flex items-center gap-2 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            Les utilisateurs concernés devront configurer leur application d'authentification à la
            prochaine connexion.
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 bg-muted rounded-lg" />
            ))}
          </div>
        ) : (
          POLICY_TOGGLES.map((toggle, idx) => {
            const enabled = policy[toggle.policyKey] as boolean;
            const isGlobal = toggle.key === 'mfa_required_all';
            return (
              <div key={toggle.key}>
                <div className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                  enabled
                    ? isGlobal
                      ? 'bg-red-50 border-red-200'
                      : 'bg-primary/5 border-primary/20'
                    : 'bg-background border-border'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {toggle.icon}
                    </div>
                    <div>
                      <Label className="font-medium flex items-center gap-2">
                        {toggle.label}
                        {isGlobal && (
                          <Badge variant="secondary" className="text-xs">Global</Badge>
                        )}
                        {enabled && (
                          <Badge className="text-xs bg-primary/20 text-primary border-primary/30">Actif</Badge>
                        )}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">{toggle.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    disabled={saving === toggle.key}
                    onCheckedChange={(val) => handleToggle(toggle.key, val)}
                  />
                </div>
                {idx < POLICY_TOGGLES.length - 1 && <Separator className="my-1 opacity-0" />}
              </div>
            );
          })
        )}

        {/* Durée de mémorisation des appareils */}
        <Separator className="mt-4" />
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-muted">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <Label className="font-medium">Durée de mémorisation des appareils</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Nombre de jours avant de redemander le MFA sur un appareil mémorisé.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={365}
              value={trustedDays}
              onChange={(e) => setTrustedDays(Number(e.target.value))}
              className="w-20 text-center"
            />
            <button
              onClick={saveTrustedDays}
              disabled={savingDays}
              className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {savingDays ? '...' : 'Sauver'}
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
